import { useState, type FormEvent } from 'react';
import { LEAD_ORIGIN_LABELS, LEAD_ORIGINS, whatsappLink, type LeadOrigin } from '@agencia/shared';
import { Alert, Button } from '@/components/ui';
import { api } from '@/lib/api';
import { errorMessage } from '@/lib/errors';
import { useApi } from '@/lib/useApi';

interface LinkRow {
  id: string;
  nome: string;
  origem: LeadOrigin;
  mensagem: string;
  ativo: boolean;
  clientes: number;
  vendas: number;
}
interface Data {
  whatsappNumero: string | null;
  links: LinkRow[];
}

const ORIGENS_LINK = LEAD_ORIGINS.filter((o) => o !== 'DESCONHECIDA');

/** Links do WhatsApp com mensagem pronta: cada um identifica de onde o cliente veio. */
export function OriginLinksPage() {
  const { data, error, setData } = useApi<Data>('/crm/origin-links');
  const [msg, setMsg] = useState<{ kind: 'error' | 'success'; text: string } | null>(null);
  const [nome, setNome] = useState('');
  const [origem, setOrigem] = useState<LeadOrigin>('ANUNCIO');
  const [mensagem, setMensagem] = useState('');
  const [numero, setNumero] = useState('');
  const [copiado, setCopiado] = useState<string | null>(null);

  async function criar(e: FormEvent) {
    e.preventDefault();
    setMsg(null);
    try {
      setData(await api.post<Data>('/crm/origin-links', { nome, origem, mensagem }));
      setNome('');
      setMensagem('');
      setMsg({ kind: 'success', text: 'Link cadastrado. Clientes que chegarem com essa mensagem já serão identificados.' });
    } catch (err) {
      setMsg({ kind: 'error', text: errorMessage(err) });
    }
  }

  async function alternar(l: LinkRow) {
    try {
      setData(await api.put<Data>(`/crm/origin-links/${l.id}`, { nome: l.nome, origem: l.origem, mensagem: l.mensagem, ativo: !l.ativo }));
    } catch (err) {
      setMsg({ kind: 'error', text: errorMessage(err) });
    }
  }

  async function salvarNumero(e: FormEvent) {
    e.preventDefault();
    try {
      setData(await api.put<Data>('/crm/whatsapp', { numero }));
      setNumero('');
    } catch (err) {
      setMsg({ kind: 'error', text: errorMessage(err) });
    }
  }

  function copiar(l: LinkRow) {
    if (!data?.whatsappNumero) return setMsg({ kind: 'error', text: 'Cadastre primeiro o número de WhatsApp da agência.' });
    void navigator.clipboard.writeText(whatsappLink(data.whatsappNumero, l.mensagem)).then(() => {
      setCopiado(l.id);
      window.setTimeout(() => setCopiado(null), 2000);
    });
  }

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Links de origem</h1>
          <p className="muted">
            Cada lugar onde a Fly Now divulga ganha um link do WhatsApp com uma mensagem pronta. Quando o cliente chama com essa mensagem, o sistema sabe de onde veio.
          </p>
        </div>
      </header>
      {(error ?? msg) && <Alert kind={msg?.kind ?? 'error'}>{error ?? msg?.text}</Alert>}

      <form className="card" onSubmit={(e) => void criar(e)}>
        <h2>Cadastrar link (novo ou que já existe)</h2>
        <p className="muted small">Para um link que vocês já usam, copie aqui exatamente a mensagem pronta que ele envia.</p>
        <div className="grid-form">
          <div className="field">
            <label htmlFor="ol-nome">Onde é usado</label>
            <input id="ol-nome" placeholder="Ex.: Anúncio São Paulo" value={nome} onChange={(e) => setNome(e.target.value)} required maxLength={80} />
          </div>
          <div className="field">
            <label htmlFor="ol-origem">Tipo de origem</label>
            <select id="ol-origem" value={origem} onChange={(e) => setOrigem(e.target.value as LeadOrigin)}>
              {ORIGENS_LINK.map((o) => (
                <option key={o} value={o}>
                  {LEAD_ORIGIN_LABELS[o]}
                </option>
              ))}
            </select>
          </div>
          <div className="field grid-form__wide">
            <label htmlFor="ol-msg">Mensagem pronta do cliente</label>
            <input id="ol-msg" placeholder="Ex.: Olá, quero solicitar uma cotação para minha viagem!" value={mensagem} onChange={(e) => setMensagem(e.target.value)} required maxLength={500} />
          </div>
          <div className="row">
            <Button type="submit">Cadastrar link</Button>
          </div>
        </div>
      </form>

      <section className="card flush">
        <h2 className="pad-h">Seus links</h2>
        {!data ? (
          <p className="muted pad">Carregando…</p>
        ) : data.links.length === 0 ? (
          <p className="muted pad">Nenhum link cadastrado ainda.</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Link</th>
                  <th scope="col">Mensagem que o cliente envia</th>
                  <th scope="col" className="num">Clientes</th>
                  <th scope="col" className="num">Vendas</th>
                  <th scope="col">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.links.map((l) => (
                  <tr key={l.id} className={l.ativo ? undefined : 'row--off'}>
                    <td>
                      <strong>{l.nome}</strong>
                      <br />
                      <span className="muted small">{LEAD_ORIGIN_LABELS[l.origem]}</span>
                    </td>
                    <td>"{l.mensagem}"</td>
                    <td className="num">{l.clientes}</td>
                    <td className="num">
                      <strong>{l.vendas}</strong>
                    </td>
                    <td className="actions">
                      <Button variant="secondary" onClick={() => copiar(l)}>
                        {copiado === l.id ? 'Copiado!' : 'Copiar link'}
                      </Button>
                      <Button variant="ghost" onClick={() => void alternar(l)}>
                        {l.ativo ? 'Desativar' : 'Reativar'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <form className="card narrow" onSubmit={(e) => void salvarNumero(e)}>
        <h2>Número de WhatsApp da agência</h2>
        <p className="muted small">
          Usado para montar os links. {data?.whatsappNumero ? `Atual: +${data.whatsappNumero}` : 'Ainda não cadastrado.'}
        </p>
        <div className="row">
          <input aria-label="Número de WhatsApp" placeholder="(75) 9 9999-9999" value={numero} onChange={(e) => setNumero(e.target.value)} required />
          <Button type="submit" variant="secondary">
            Salvar número
          </Button>
        </div>
      </form>
    </div>
  );
}
